import { Link } from "react-router-dom";
import {
    ArrowUpRight,
    CheckCircle2,
    AlertCircle,
    MessageSquare,
    FileText,
} from "lucide-react";

/**
 * "Your next move" — a single, primary call-to-action card that tells the
 * client exactly what to DO right now. Picks the highest-priority signal:
 *   1. Unread chat messages
 *   2. Milestones awaiting approval
 *   3. Outstanding invoices
 *   4. Else → "All caught up" celebratory state
 */
const ACCENT_CLASSES = {
    gold: {
        wrapBorder: "border-gold/40 bg-gold/[0.05]",
        glow: "shadow-[0_0_60px_-25px_rgba(212,175,106,0.45)]",
        iconBox: "border-gold/40 bg-gold/10",
        iconColor: "text-gold",
        eyebrow: "text-gold",
        cta: "bg-gold text-ink hover:bg-gold-light transition-colors",
    },
    ember: {
        wrapBorder: "border-ember/40 bg-ember/[0.05]",
        glow: "shadow-[0_0_60px_-25px_rgba(200,122,56,0.4)]",
        iconBox: "border-ember/40 bg-ember/10",
        iconColor: "text-ember",
        eyebrow: "text-ember",
        cta: "btn-ember",
    },
};

export default function NextActionCard({
    unreadChat = 0,
    needsApproval = 0,
    outstanding = 0,
    managerName,
    tier = "starter",
}) {
    const action = pickAction({ unreadChat, needsApproval, outstanding, managerName });
    const isPremium = tier === "sprint" || tier === "studio";
    const accentKey = isPremium ? "gold" : "ember";
    const a = ACCENT_CLASSES[accentKey];

    const isCalm = action.tone === "calm";

    return (
        <section
            data-testid="next-action-card"
            className={`relative border ${
                isCalm
                    ? "border-emerald-400/30 bg-emerald-400/[0.04]"
                    : a.wrapBorder
            } px-6 lg:px-8 py-6 mb-12 ${isPremium && !isCalm ? a.glow : ""}`}
        >
            <div className="flex items-start justify-between gap-6 flex-wrap">
                <div className="flex items-start gap-4 flex-1 min-w-0">
                    <div
                        className={`shrink-0 w-11 h-11 inline-flex items-center justify-center border ${
                            isCalm
                                ? "border-emerald-400/30 bg-emerald-400/10"
                                : a.iconBox
                        }`}
                    >
                        <action.Icon
                            className={`w-5 h-5 ${
                                isCalm ? "text-emerald-400" : a.iconColor
                            }`}
                        />
                    </div>
                    <div className="min-w-0">
                        <div
                            data-testid="next-action-eyebrow"
                            className={`text-[10px] uppercase tracking-[0.3em] mb-1.5 ${
                                isCalm ? "text-emerald-400" : a.eyebrow
                            }`}
                        >
                            {action.eyebrow}
                        </div>
                        <h2
                            data-testid="next-action-title"
                            className="font-display text-bone text-xl lg:text-2xl tracking-tight leading-snug"
                        >
                            {action.title}
                        </h2>
                        {action.subtitle && (
                            <p className="text-bone-300 text-sm mt-1.5 leading-relaxed">
                                {action.subtitle}
                            </p>
                        )}
                    </div>
                </div>
                {action.cta && (
                    <Link
                        to={action.cta.to}
                        data-testid="next-action-cta"
                        className={`shrink-0 inline-flex items-center gap-2 h-11 px-5 text-xs uppercase tracking-[0.2em] font-medium ${
                            isCalm
                                ? "border border-emerald-400/30 text-emerald-300 hover:border-emerald-400/60 transition-colors"
                                : a.cta
                        }`}
                    >
                        {action.cta.label}
                        <ArrowUpRight className="w-4 h-4" />
                    </Link>
                )}
            </div>
        </section>
    );
}

function pickAction({ unreadChat, needsApproval, outstanding, managerName }) {
    if (unreadChat > 0) {
        return {
            Icon: MessageSquare,
            eyebrow: "New message",
            title: `${managerName ? managerName : "Your manager"} sent you ${unreadChat} new message${unreadChat === 1 ? "" : "s"}.`,
            subtitle: "Tap reply to keep things moving.",
            cta: { to: "#client-chat-section", label: "Open chat" },
            tone: "accent",
        };
    }
    if (needsApproval > 0) {
        return {
            Icon: AlertCircle,
            eyebrow: "Awaiting your approval",
            title: `${needsApproval} milestone${needsApproval === 1 ? "" : "s"} ready for your green light.`,
            subtitle: "Review and approve so your team can move to the next step.",
            cta: { to: "#projects", label: "Review now" },
            tone: "accent",
        };
    }
    if (outstanding > 0) {
        return {
            Icon: FileText,
            eyebrow: "Open invoice",
            title: `You have $${outstanding.toFixed(2)} outstanding.`,
            subtitle: "Settle it whenever — bank transfer details inside.",
            cta: { to: "/portal/invoices", label: "View invoices" },
            tone: "accent",
        };
    }
    return {
        Icon: CheckCircle2,
        eyebrow: "All caught up",
        title: "Nothing on your plate — we're driving things forward.",
        subtitle:
            "We'll let you know the moment something needs your eyes. In the meantime, your team is creating.",
        cta: null,
        tone: "calm",
    };
}

export { NextActionCard };
